import { useCallback, useMemo, useState } from 'react'
import { NavigateOptions, useParams, useNavigate } from 'react-router-dom'
import queryString from 'query-string'
import {
  getCreateChatContextRequest,
  getDefaultChatContext,
} from 'src/components/Reusable/ChatContext/chatContext.utils'
import {
  ChatContext,
  useUpdateChatContext,
} from 'src/components/Reusable/ChatContext/useChatContext.hooks'
import { ChatServicePromiseClient } from '@trustero/trustero-api-web/lib/chat/chat_grpc_web_pb'
import { ClientMessageType } from '@trustero/trustero-api-web/lib/tichatapi/agent_pb'
import { useAuthorizedGrpcClient } from 'src/adapter'
import log from 'loglevel'
import {
  AppCopilotReferringContextMetadata as AppCopilotReferringContextMetadataProto,
  AnswerReactionType,
  Chat,
  ChatMessage,
  CreateAppCopilotChatRequest,
  CreateAppCopilotChatResponse,
  CreateChatRequest,
  DeleteChatRequest,
  DeleteReactionToAnswerRequest,
  GetChatMessagesSinceRequest,
  GetChatRequest,
  GetChatResponse,
  GetIntermediateStatusResponse,
  ListAppCopilotChatsRequest,
  ListAppCopilotChatsResponse,
  ReactToAnswerRequest,
  ReferringContext as ReferringContextProto,
  SendChatMessageRequest,
  UpdateChatRequest,
} from '@trustero/trustero-api-web/lib/chat/chat_pb'
import { NewGrpcResponse } from 'src/components/async/hooks/types'
import { GrpcResponse } from 'src/components/async/hooks/types'
import { useSwrImmutableGrpc } from 'src/components/async/useSwrImmutableGrpc'
import { useSwrGrpc } from 'src/components/async/useSwrGrpc'
import { generatePermalink } from 'src/components/PageLayout/Permalink/generatePermalink'
import {
  FileUploadBody,
  MODEL_TYPE,
} from '@trustero/trustero-api-web/lib/common/model_pb'
import {
  ReferringContext,
  ReferringContextMetadata,
} from 'src/components/Copilot/AppCopilot/AppCopilot.types'
import { PlaybookInputs } from '@trustero/trustero-api-web/lib/playbook/playbook_pb'
import { TrusteroIntelligenceAbsoluteRoutes } from 'src/components/Reusable/RootPage/RootPage.constants'
import { getBytesPayload } from 'src/Utils/File/file'

export const DEFAULT_IS_PRIVATE = false

const toAppCopilotMetadataProto = (
  referringContextMetadata?: ReferringContextMetadata,
): AppCopilotReferringContextMetadataProto =>
  new AppCopilotReferringContextMetadataProto()
    .setFilterQuery(referringContextMetadata?.filterQuery || '')
    .setResultIdsList(referringContextMetadata?.resultIds || [])
    .setResultsCount(referringContextMetadata?.resultsCount || 0)

export type UseCreateChatResponse = {
  chat: Chat
}

export const useCreateChat = (): (() => Promise<UseCreateChatResponse>) => {
  const client = useAuthorizedGrpcClient(ChatServicePromiseClient)

  return useCallback(async (): Promise<UseCreateChatResponse> => {
    const request = new CreateChatRequest()
    request.setChatContext(getCreateChatContextRequest(getDefaultChatContext()))
    request.setIsPrivate(DEFAULT_IS_PRIVATE)
    const response = await client.createChat(request)

    const chat = response.getChat()

    if (chat) {
      return {
        chat,
      }
    }

    throw new Error('Failed to create chat')
  }, [client])
}

export const useGetChat = (chatId: string): GrpcResponse<GetChatResponse> => {
  const shouldFetch = !!(chatId && chatId.length)
  const request = new GetChatRequest().setChatId(chatId || '')
  const { response } = useSwrImmutableGrpc(
    ChatServicePromiseClient.prototype.getChat,
    request,
    shouldFetch,
  )

  return NewGrpcResponse(response)
}

export type UseSendChatMessageProps = {
  chat: Chat
  prompt: string
  messageType?: ClientMessageType
  fileUploads?: File[]
  playbookInputs?: PlaybookInputs
  onSuccess: (questionId: string) => Promise<void>
  onError: (error: unknown) => void
}

export const useSendChatMessage = (): ((
  props: UseSendChatMessageProps,
) => Promise<void>) => {
  const client = useAuthorizedGrpcClient(ChatServicePromiseClient)

  return useCallback(
    async ({
      chat,
      prompt,
      onSuccess,
      onError,
      messageType,
      fileUploads,
      playbookInputs,
    }: UseSendChatMessageProps) => {
      const request = new SendChatMessageRequest()
        .setChatId(chat.getId())
        .setQuestion(prompt)
      if (messageType) {
        request.setMessageType(messageType)
      }
      if (fileUploads) {
        const fileUploadsList = []
        for (const file of fileUploads) {
          const fileBytes = await getBytesPayload(file)
          fileUploadsList.push(
            new FileUploadBody()
              .setFileName(file.name)
              .setMimeType(file.type)
              .setFile(fileBytes)
              .setCaption(file.name),
          )
        }
        request.setFileUploadsList(fileUploadsList)
      }
      if (playbookInputs) {
        request.setPlaybookInputs(playbookInputs)
      }
      try {
        const response = await client.sendChatMessage(request)
        onSuccess(response.getQuestionId())
      } catch (error) {
        onError(error)
      }
    },
    [client],
  )
}

export const useGetRecentMessages = (
  chatId: string,
): ((since: number) => Promise<{
  messages: ChatMessage[]
  chatTitle: string
  intermediateStatus: GetIntermediateStatusResponse | undefined
  aiQuestionCredits: number
}>) => {
  const client = useAuthorizedGrpcClient(ChatServicePromiseClient)

  return useCallback(
    async (since: number) => {
      const request = new GetChatMessagesSinceRequest()
        .setSince(since)
        .setChatId(chatId)
      const response = await client.getChatMessagesSince(request)
      return {
        messages: response.getMessagesList(),
        chatTitle: response.getChatTitle(),
        intermediateStatus: response.getIntermediateStatus(),
        aiQuestionCredits: response.getAiQuestionCredits(),
      }
    },
    [client, chatId],
  )
}

export const useDeleteChat = (): ((chatId: string) => Promise<void>) => {
  const client = useAuthorizedGrpcClient(ChatServicePromiseClient)

  return useCallback(
    async (chatId: string) => {
      const request = new DeleteChatRequest().setChatId(chatId)
      await client.deleteChat(request)
    },
    [client],
  )
}

export const useListAppCopilotChats = (
  referringContext: ReferringContext,
): GrpcResponse<ListAppCopilotChatsResponse> => {
  const refContext = new ReferringContextProto()
    .setObjectType(referringContext.objectType)
    .setObjectId(referringContext.objectId || '')
  const request = new ListAppCopilotChatsRequest().setReferringContext(
    refContext,
  )
  const { response } = useSwrGrpc(
    ChatServicePromiseClient.prototype.listAppCopilotChats,
    request,
  )
  return NewGrpcResponse(response)
}

export const useCreateAppCopilotChat = (): ((
  referringContext: ReferringContext,
  title: string,
  referringContextMetadata?: ReferringContextMetadata,
) => Promise<{ chat: Chat }>) => {
  const client = useAuthorizedGrpcClient(ChatServicePromiseClient)

  return useCallback(
    async (
      referringContext: ReferringContext,
      title: string,
      referringContextMetadata?: ReferringContextMetadata,
    ) => {
      const refContext = new ReferringContextProto()
        .setObjectType(referringContext.objectType)
        .setObjectId(referringContext.objectId || '')
      const request = new CreateAppCopilotChatRequest()
        .setReferringContext(refContext)
        .setTitle(title)
      const metadata = toAppCopilotMetadataProto(referringContextMetadata)
      request.setReferringContextMetadata(metadata)
      const response: CreateAppCopilotChatResponse =
        await client.createAppCopilotChat(request)
      const chat = response.getChat()
      if (!chat) {
        throw new Error('Failed to create app copilot chat')
      }
      return { chat }
    },
    [client],
  )
}

export const useChatPermalink = (
  chatId: string,
  artifactId?: string,
): string => {
  const params = useParams()

  return useMemo(
    () =>
      generatePermalink({
        pageContext: params.pageContext ?? '',
        modelType: MODEL_TYPE.CHAT,
        modelId: chatId,
        isInternalLink: false,
        includeSearchParams: artifactId ? true : undefined,
        search: artifactId
          ? queryString.parse(`?artifact=${artifactId}`, {
              arrayFormat: 'bracket',
            })
          : undefined,
      }),
    [chatId, params.pageContext, artifactId],
  )
}

export const useNavigateToChat = (): ((
  chatId: string,
  messageId?: string,
  options?: NavigateOptions,
) => void) => {
  const navigate = useNavigate()
  const params = useParams()

  return useCallback(
    (chatId: string, messageId?: string, options?: { replace?: boolean }) => {
      const basePath = `/${params.pageContext}/${TrusteroIntelligenceAbsoluteRoutes.SHOW}/${chatId}`
      const url = messageId ? `${basePath}?messageId=${messageId}` : basePath
      navigate(url, options)
    },
    [navigate, params.pageContext],
  )
}

export const useUpdateChat = (): ((
  chat: Chat,
  playbookInputs?: PlaybookInputs,
) => Promise<void>) => {
  const client = useAuthorizedGrpcClient(ChatServicePromiseClient)

  return useCallback(
    async (chat: Chat, playbookInputs?: PlaybookInputs) => {
      const request = new UpdateChatRequest()
        .setChatId(chat.getId())
        .setIsPrivate(chat.getIsPrivate())
        .setName(chat.getName())
        .setModel(chat.getModel())
        .setLeadPrompt(chat.getLeadPrompt())
        .setInitiatePrompt(chat.getInitiatePrompt())
      if (playbookInputs) {
        request.setPlaybookInputs(playbookInputs)
      }
      await client.updateChat(request)
    },
    [client],
  )
}

export type UseEditChatContextProps = {
  chatContextId?: string
  setOriginalContext?: (context: ChatContext) => void
}

export const useEditChatContext = ({
  chatContextId,
  setOriginalContext,
}: UseEditChatContextProps): {
  setContext: (chatContext: ChatContext) => Promise<void>
  isUpdatingContext: boolean
  errorUpdatingContext: string | null
} => {
  const mutate = useUpdateChatContext()
  const [isUpdatingContext, setIsUpdatingContext] = useState(false)
  const [errorUpdatingContext, setErrorUpdatingContext] = useState<
    string | null
  >(null)

  const setContext = useCallback(
    async (newContext: ChatContext) => {
      try {
        if (chatContextId) {
          setIsUpdatingContext(true)
          await mutate(chatContextId, newContext)
        }
        setOriginalContext?.(newContext)
      } catch (error) {
        log.error('Failed updating chat context', error)
        setErrorUpdatingContext?.(
          error instanceof Error
            ? error.message
            : 'Error updating chat context',
        )
      } finally {
        setIsUpdatingContext?.(false)
      }
    },
    [setOriginalContext, mutate, chatContextId],
  )

  return useMemo(
    () => ({
      setContext,
      isUpdatingContext,
      errorUpdatingContext,
    }),
    [setContext, isUpdatingContext, errorUpdatingContext],
  )
}

export const useReactToAnswer = (): ((
  answerId: string,
  reaction: AnswerReactionType,
  feedback: string,
) => Promise<void>) => {
  const client = useAuthorizedGrpcClient(ChatServicePromiseClient)

  return useCallback(
    async (
      answerId: string,
      reaction: AnswerReactionType,
      feedback: string,
    ) => {
      const request = new ReactToAnswerRequest()
      request.setAnswerId(answerId)
      request.setReaction(reaction)
      request.setFeedback(feedback)
      await client.reactToAnswer(request)
    },
    [client],
  )
}

export const useDeleteReactionToAnswer = (): ((
  answerId: string,
) => Promise<void>) => {
  const client = useAuthorizedGrpcClient(ChatServicePromiseClient)

  return useCallback(
    async (answerId: string) => {
      const request = new DeleteReactionToAnswerRequest()
      request.setAnswerId(answerId)
      await client.deleteReactionToAnswer(request)
    },
    [client],
  )
}
